// src/utils/notificacionesPagosService.js
// Servicio en segundo plano para detectar pagos en línea y registrar notificaciones en el Centro de Mensajes

import { obtenerPagos } from '../api/pagos.js';
import {
  agregarNotificacion,
  resolverNotificacionPago,
  eliminarNotificacion,
  cargarNotificaciones,
  obtenerPagosDescartados,
  olvidarPagoDescartado
} from './notificaciones.js';
import { playNotificationChime } from './audioAlert.js';
import { isAuthenticated } from '../api/index.js';
import router from '../router/index.js';
import { useToast } from 'vue-toastification';

let intervalId = null;
let toastInstance = null;

const STORAGE_RECORDATORIOS = 'admin_pagos_ultimo_recordatorio';
const INTERVALO_RECORDATORIO_MS = 20 * 60 * 1000; // 20 minutos de intervalo de recordatorio

function getToast() {
  if (!toastInstance) {
    try {
      toastInstance = useToast();
    } catch (e) {
      // Si aún no está montado el contexto de vue
    }
  }
  return toastInstance;
}

function getRecordatorios() {
  try {
    const raw = localStorage.getItem(STORAGE_RECORDATORIOS);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function setRecordatorio(idPago, time) {
  try {
    const map = getRecordatorios();
    map[String(idPago)] = time;
    localStorage.setItem(STORAGE_RECORDATORIOS, JSON.stringify(map));
  } catch {}
}

function limpiarRecordatorio(idPago) {
  try {
    const map = getRecordatorios();
    delete map[String(idPago)];
    localStorage.setItem(STORAGE_RECORDATORIOS, JSON.stringify(map));
  } catch {}
}

/**
 * Determina con precisión si un pago proviene del portal web público (proyecto web).
 * Solo los pagos de servicios/recepción registrados por clientes en la web
 * deben generar alertas de "Llegó un pago online de servicios".
 * Las ventas (POS / tienda física) y créditos presenciales NUNCA son pagos online de la web.
 */
export function esPagoOnlineWeb(p) {
  if (!p) return false;

  // Si el backend explícitamente marcó esOnline
  if (p.esOnline === true || p.EsOnline === true) return true;

  // Una Venta o Cuota realizada en tienda NUNCA es un pago online de servicios de la web
  const tipoPagoLower = (p.tipoPago ?? p.TipoPago ?? p.tipoDocumentoPago ?? '').toString().toLowerCase();
  if (tipoPagoLower === 'venta' || p.idVenta || p.IdVenta) return false;
  if (tipoPagoLower === 'credito' || tipoPagoLower === 'cuota' || p.idCuota || p.IdCuota) return false;

  const desc = (p.descripcion ?? p.Descripcion ?? p.observaciones ?? p.Observaciones ?? '').toString();
  const numRecibo = (p.numeroRecibo ?? p.NumeroRecibo ?? '').toString();
  const usuario = (p.usuario ?? p.Usuario ?? '').toString().toLowerCase();

  // Pagos registrados desde el asistente web público (proyecto web)
  const tieneMarcaWeb = desc.includes('[PAGO WEB]') || numRecibo.startsWith('WEB-');
  const usuarioEsWeb = usuario.includes('sistema web');

  return tieneMarcaWeb || (usuarioEsWeb && (p.idRecepcionEquipo || tipoPagoLower.includes('recepcion')));
}

export async function verificarPagosEnLinea() {
  if (!isAuthenticated()) return;

  try {
    const res = await obtenerPagos();
    const lista = res?.success && Array.isArray(res.data) ? res.data : (Array.isArray(res) ? res : []);

    // Solo pagos online reales provenientes de la web que estén pendientes de verificación
    const pendientes = lista.filter(p => {
      const estado = (p.estado ?? p.Estado ?? (p.idEstadoPago === 1 ? 'completado' : p.idEstadoPago === 2 ? 'pendiente' : '')).toString().toLowerCase().trim();
      return estado === 'pendiente' && esPagoOnlineWeb(p);
    });

    const descartados = obtenerPagosDescartados().map(id => String(id));

    // Solo considerar activos los pendientes que no hayan sido descartados por el usuario
    const pendientesActivos = pendientes.filter(p => {
      const idPago = String(p.idPago ?? p.IdPago);
      return !descartados.includes(idPago);
    });

    // Emitir conteo para que la campana del Header y mobile se actualicen
    window.dispatchEvent(new CustomEvent('pagosPendientesActualizados', {
      detail: {
        cantidad: pendientesActivos.length,
        pagos: pendientesActivos
      }
    }));

    const notifsActuales = cargarNotificaciones();
    const ahora = Date.now();
    const recordatorios = getRecordatorios();

    for (const p of pendientes) {
      const idPago = p.idPago ?? p.IdPago;
      const idStr = String(idPago);
      const key = `pago-${idPago}`;

      // Si el usuario eliminó/descartó esta notificación, NO volver a alertar ni mostrar
      if (descartados.includes(idStr)) {
        continue;
      }

      const notifExistente = notifsActuales.find(n => n.idPago === idPago || n.id === key);

      const montoUsd = (parseFloat(p.monto_usd ?? p.montoUsd ?? p.monto ?? 0) || 0).toFixed(2);
      const metodo = p.metodo_pago || p.metodoPago || 'Pago Móvil';
      const numOrd = p.codigoRecepcion || p.numeroOrden || p.CodigoRecepcion || p.NumeroOrden || (p.idReferencia ? `${p.idReferencia}` : null);
      const refTexto = numOrd ? `Orden #${numOrd}` : (p.referencia ? `Ref: ${p.referencia}` : 'Servicios');

      if (!notifExistente) {
        // Primera vez que se detecta: registrar en Centro de Mensajes
        agregarNotificacion({
          idPago: idPago,
          modulo: 'pago',
          accion: 'Verifique',
          titulo: 'Llegó un pago online de servicios',
          detalle: `${refTexto} · $${montoUsd} USD · ${metodo}. Por favor verifique en el módulo de pagos.`,
          ruta: '/pagos'
        });

        setRecordatorio(idStr, ahora);

        // Alerta sonora sutil
        playNotificationChime();

        // Toast temporal discreto (desaparece a los 6 seg para no estorbar)
        const toast = getToast();
        if (toast) {
          toast.info(`💳 Llegó un pago online de servicios (${refTexto} - $${montoUsd} USD). Por favor verifique.`, {
            timeout: 6000,
            onClick: () => {
              if (router) router.push('/pagos');
            }
          });
        }
      } else {
        // Ya existe en Centro de Mensajes y el usuario NO la ha borrado
        // Si aún no está verificada y han pasado 20 minutos, enviar recordatorio
        const ultimoAviso = recordatorios[idStr] || 0;
        const sigueSinVerificar = !notifExistente.leida || notifExistente.accion !== 'Verificado';

        if (sigueSinVerificar && (ahora - ultimoAviso >= INTERVALO_RECORDATORIO_MS)) {
          setRecordatorio(idStr, ahora);

          // Alerta sonora sutil de recordatorio
          playNotificationChime();

          const toast = getToast();
          if (toast) {
            toast.info(`⏰ Recordatorio: Tiene un pago online de servicios pendiente de verificación (${refTexto} - $${montoUsd} USD).`, {
              timeout: 6000,
              onClick: () => {
                if (router) router.push('/pagos');
              }
            });
          }
        }
      }
    }

    // Si algún pago previamente registrado ya fue completado en /pagos, o no corresponde a un pago online de la web, limpiar
    for (const n of notifsActuales) {
      if (n.idPago) {
        const pagoDb = lista.find(p => String(p.idPago ?? p.IdPago) === String(n.idPago));
        if (pagoDb) {
          // Si no es un pago online real de la web (ej. es una venta física en tienda), eliminar notificación espuria
          if (!esPagoOnlineWeb(pagoDb)) {
            eliminarNotificacion(n.id);
            limpiarRecordatorio(n.idPago);
            continue;
          }

          const estado = (pagoDb.estado ?? pagoDb.Estado ?? (pagoDb.idEstadoPago === 1 ? 'completado' : '')).toString().toLowerCase().trim();
          if (estado === 'completado') {
            if (!n.leida || n.accion !== 'Verificado') {
              resolverNotificacionPago(n.idPago);
            }
            limpiarRecordatorio(n.idPago);
            olvidarPagoDescartado(n.idPago);
          }
        }
      }
    }
  } catch (e) {
    console.warn('Error verificando pagos en línea:', e);
  }
}

export function iniciarServicioNotificacionesPagos() {
  if (intervalId) clearInterval(intervalId);

  // Primera verificación al iniciar
  verificarPagosEnLinea();

  // Polling silencioso cada 20 segundos
  intervalId = setInterval(() => {
    verificarPagosEnLinea();
  }, 20000);

  // También verificar inmediatamente cuando la ventana recupera el foco o se cambia de estado en pagos
  if (typeof window !== 'undefined') {
    window.addEventListener('pagoEstadoCambiado', verificarPagosEnLinea);
    window.addEventListener('pagosActualizados', verificarPagosEnLinea);
    window.addEventListener('focus', verificarPagosEnLinea);
  }
}

export function detenerServicioNotificacionesPagos() {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
  if (typeof window !== 'undefined') {
    window.removeEventListener('pagoEstadoCambiado', verificarPagosEnLinea);
    window.removeEventListener('pagosActualizados', verificarPagosEnLinea);
    window.removeEventListener('focus', verificarPagosEnLinea);
  }
}
